import { useState, useRef } from "react";
import { Textarea } from "@/components/ui/textarea";
import { ListChecks, Code, AlignLeft, MessageSquare } from "lucide-react";
import { VoiceRecorder } from "./VoiceRecorder";

/**
 * StructuredResponseRenderer — Multi-part response form
 * 
 * Supports TWO formats:
 *
 * FORMAT 1 (existing — parts array):
 * {
 *   type: "structured_response",
 *   id: "sr_1",
 *   title: "Your Analysis",
 *   parts: [
 *     { label: "Explain your approach", type: "text", placeholder: "Describe..." },
 *     { label: "Implementation", type: "code", language: "python", placeholder: "..." }
 *   ]
 * }
 *
 * FORMAT 2 (HOS flat — prompt + response_type + max_words):
 * {
 *   type: "structured_response",
 *   id: "hos_act1_s1",
 *   section_number: 1,
 *   title: "Initial Assessment",
 *   prompt: "What type of attack is this? Classify it by...",
 *   response_type: "long_text",
 *   max_words: 350,
 *   scoring_dimensions: [...]  // NOT displayed
 * }
 */
export function StructuredResponseRenderer({ item, index, responses, onResponseChange, onPaste, voiceEnabled, token, sectionId, locked }) {
  const id = item.id || `structured_response_${index}`;
  const title = item.title || "";

  // Detect format: if item has 'prompt' + 'response_type' → flat HOS format
  const isFlatFormat = item.prompt && item.response_type;

  if (isFlatFormat) {
    return <FlatStructuredResponse item={item} index={index} id={id} title={title}
      responses={responses} onResponseChange={onResponseChange} onPaste={onPaste}
      voiceEnabled={voiceEnabled} token={token} sectionId={sectionId} locked={locked} />;
  }

  // Original parts-based format
  const parts = item.parts || [];

  const getPartKey = (partIdx) => `${id}_part_${partIdx}`;

  const handleCodeKeyDown = (e, partIdx) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const key = getPartKey(partIdx);
      const val = responses[key] || "";
      const newVal = val.substring(0, start) + "    " + val.substring(end);
      onResponseChange(key, newVal);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 4;
      });
    }
  };

  return (
    <div key={index} className="my-6" data-testid={`structured-response-${index}`}>
      {title && (
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-5 h-5 text-[#00AEEF]" />
          <h4 className="font-semibold text-[#3b3b3b]">{title}</h4>
        </div>
      )}
      <div className="space-y-4 ml-2 border-l-2 border-[#00AEEF]/20 pl-4">
        {parts.map((part, partIdx) => {
          const partKey = getPartKey(partIdx);
          const partType = part.type || "text";
          const partValue = responses[partKey] || "";

          return (
            <div key={partIdx} data-testid={`structured-part-${index}-${partIdx}`}>
              {/* Part label */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
                  {partType === "code" ? (
                    <Code className="w-3.5 h-3.5 text-[#00AEEF]" />
                  ) : (
                    <AlignLeft className="w-3.5 h-3.5 text-[#00AEEF]" />
                  )}
                </div>
                <label className="text-sm font-medium text-[#3b3b3b]">
                  {part.label}
                  {part.required && <span className="text-red-400 ml-1">*</span>}
                </label>
              </div>

              {/* Part input */}
              {partType === "code" ? (
                <div className="ml-8">
                  <div className="bg-[#1e1e2e] rounded-lg border border-[#2a2a3e] overflow-hidden">
                    {part.language && (
                      <div className="px-3 py-1.5 border-b border-[#2a2a3e] flex items-center">
                        <span className="text-xs text-gray-500 uppercase tracking-wide">{part.language}</span>
                      </div>
                    )}
                    <textarea
                      value={partValue}
                      onChange={(e) => onResponseChange(partKey, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(e, partIdx)}
                      onPaste={(e) => {
                        const pasted = e.clipboardData?.getData("text") || "";
                        if (pasted && onPaste) onPaste(partKey, pasted);
                      }}
                      placeholder={part.placeholder || "Write your code here..."}
                      spellCheck={false}
                      className="w-full bg-transparent text-gray-200 font-mono text-[13px] leading-6 p-3 resize-none outline-none placeholder:text-gray-600 min-h-[150px]"
                      data-testid={`structured-code-${index}-${partIdx}`}
                    />
                  </div>
                  {voiceEnabled && (
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-gray-400 italic">or</span>
                      <VoiceRecorder
                        token={token}
                        questionId={partKey}
                        sectionId={sectionId}
                        onTranscription={(text) => {
                          const existing = responses[partKey] || "";
                          if (!existing.trim()) onResponseChange(partKey, text);
                        }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="ml-8">
                  <Textarea
                    value={partValue}
                    onChange={(e) => onResponseChange(partKey, e.target.value)}
                    onPaste={(e) => {
                      const pasted = e.clipboardData?.getData("text") || "";
                      if (pasted && onPaste) onPaste(partKey, pasted);
                    }}
                    placeholder={part.placeholder || "Enter your response..."}
                    className="min-h-[100px] text-sm"
                    data-testid={`structured-text-${index}-${partIdx}`}
                  />
                  {voiceEnabled && (
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-gray-400 italic">or</span>
                      <VoiceRecorder
                        token={token}
                        questionId={partKey}
                        sectionId={sectionId}
                        onTranscription={(text) => {
                          const existing = responses[partKey] || "";
                          if (!existing.trim()) onResponseChange(partKey, text);
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══ Flat HOS Format ═══ */
function FlatStructuredResponse({ item, index, id, title, responses, onResponseChange, onPaste, voiceEnabled, token, sectionId, locked }) {
  const fieldKey = id;
  const fieldValue = responses[fieldKey] || "";
  const maxWords = item.max_words || 0;
  const responseType = item.response_type || "long_text";
  const sectionNumber = item.section_number;

  const wordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).length;
  };

  const currentWords = wordCount(fieldValue);
  const isOverLimit = maxWords > 0 && currentWords > maxWords;

  return (
    <div key={index} className={`my-6 ${locked ? "opacity-60 pointer-events-none" : ""}`} data-testid={`structured-response-${index}`}>
      {/* Title header */}
      {title && (
        <div className="flex items-center gap-3 mb-3">
          <div className="w-7 h-7 rounded-full bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
            {sectionNumber ? (
              <span className="text-xs font-bold text-[#00AEEF]">{sectionNumber}</span>
            ) : (
              <ListChecks className="w-4 h-4 text-[#00AEEF]" />
            )}
          </div>
          <h4 className="font-semibold text-[#3b3b3b]">{title}</h4>
        </div>
      )}

      {/* Prompt */}
      {item.prompt && (
        <div className="ml-2 border-l-2 border-[#00AEEF]/20 pl-4 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="w-4 h-4 text-[#00AEEF]" />
            <span className="text-xs font-semibold text-[#1B3A5C] uppercase tracking-wider">Prompt</span>
          </div>
          <p className="text-sm text-[#3b3b3b] leading-relaxed">{item.prompt}</p>
        </div>
      )}

      {/* Response input */}
      <div className="ml-2 border-l-2 border-[#00AEEF]/20 pl-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#00AEEF]/10 flex items-center justify-center flex-shrink-0">
              {responseType === "code" ? (
                <Code className="w-3.5 h-3.5 text-[#00AEEF]" />
              ) : (
                <AlignLeft className="w-3.5 h-3.5 text-[#00AEEF]" />
              )}
            </div>
            <label className="text-sm font-medium text-[#3b3b3b]">Your Response</label>
          </div>
          {maxWords > 0 && (
            <span className={`text-xs font-mono ${isOverLimit ? "text-red-500 font-semibold" : "text-gray-400"}`}>
              {currentWords} / {maxWords} words
            </span>
          )}
        </div>

        {responseType === "code" ? (
          <div className="bg-[#1e1e2e] rounded-lg border border-[#2a2a3e] overflow-hidden">
            <textarea
              value={fieldValue}
              onChange={(e) => !locked && onResponseChange(fieldKey, e.target.value)}
              onPaste={(e) => {
                const pasted = e.clipboardData?.getData("text") || "";
                if (pasted && onPaste && !locked) onPaste(fieldKey, pasted);
              }}
              placeholder="Write your code here..."
              spellCheck={false}
              readOnly={locked}
              className="w-full bg-transparent text-gray-200 font-mono text-[13px] leading-6 p-3 resize-none outline-none placeholder:text-gray-600 min-h-[150px]"
              data-testid={`structured-flat-code-${index}`}
            />
          </div>
        ) : (
          <Textarea
            value={fieldValue}
            onChange={(e) => !locked && onResponseChange(fieldKey, e.target.value)}
            onPaste={(e) => {
              const pasted = e.clipboardData?.getData("text") || "";
              if (pasted && onPaste && !locked) onPaste(fieldKey, pasted);
            }}
            placeholder="Enter your response..."
            readOnly={locked}
            className={`min-h-[120px] text-sm ${isOverLimit ? "border-red-300 focus:ring-red-200" : ""}`}
            data-testid={`structured-flat-text-${index}`}
          />
        )}
        {voiceEnabled && (
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-gray-400 italic">or</span>
            <VoiceRecorder
              token={token}
              questionId={fieldKey}
              sectionId={sectionId}
              onTranscription={(text) => {
                const existing = responses[fieldKey] || "";
                if (!existing.trim()) onResponseChange(fieldKey, text);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
